/* Turns the QR code SVG from POST /api/auth/mfa/enroll into safe DOM nodes.
   The page runs under a strict CSP (default-src 'self'): an <img src="data:…"> is blocked, and even parsing the markup
   with DOMParser makes the browser refuse (and report) every inline style attribute. So the markup is read by a small
   tokenizer here, filtered against an allowlist, and rebuilt with createElementNS:
   - only shape elements (svg, g, rect, path, polygon, polyline, circle, ellipse, line, title, desc) are kept;
     anything else is dropped with everything inside it (script, foreignObject, a, use, image, style …)
   - only geometry and paint attributes are kept, with plain values (no url(), javascript:, quotes or brackets)
   - fill/stroke written as inline style become presentation attributes; no style attribute is ever set */

export type SvgNode = { tag: string; attrs: Record<string, string>; children: SvgNode[]; text?: string };

const SVG_NS = 'http://www.w3.org/2000/svg';
const TAGS = new Set(['svg', 'g', 'rect', 'path', 'polygon', 'polyline', 'circle', 'ellipse', 'line', 'title', 'desc']);
const ATTRS = new Set(['viewBox', 'width', 'height', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'd', 'points',
  'fill', 'stroke', 'stroke-width', 'fill-rule', 'clip-rule', 'shape-rendering', 'transform', 'opacity', 'fill-opacity',
  'stroke-opacity', 'preserveAspectRatio', 'version']);
const STYLE_PROPS = new Set(['fill', 'stroke', 'stroke-width', 'fill-opacity', 'stroke-opacity', 'opacity', 'shape-rendering']);
const SAFE_VALUE = /^[#\w\s.,%()+-]*$/;
const BAD_VALUE = /url\s*\(|javascript:|data:|expression\s*\(|[<>"'`\\]/i;
const MAX_TOKENS = 60000;

/* GoTrue may send markup or a data: URL */
export function svgMarkup(input: string): string {
  const s = (input || '').trim();
  if (!/^data:image\/svg\+xml/i.test(s)) return s;
  const comma = s.indexOf(',');
  if (comma < 0) return '';
  const meta = s.slice(0, comma), payload = s.slice(comma + 1);
  try {
    return /;base64/i.test(meta) ? atob(payload) : decodeURIComponent(payload);
  } catch { return payload; }
}

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
const decode = (s: string) => s.replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (m, e: string) => {
  if (e[0] === '#') {
    const n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
    return Number.isFinite(n) && n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
  }
  return ENTITIES[e.toLowerCase()] ?? m;
});

function parseAttrs(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const m of text.matchAll(/([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+)))?/g)) out[m[1]] = decode(m[2] ?? m[3] ?? m[4] ?? '');
  return out;
}

/* a forgiving tokenizer for the XML a QR library writes; returns the first <svg> element, or null */
export function parseSvg(input: string): SvgNode | null {
  const s = svgMarkup(input);
  if (!s || s.length > 2_000_000) return null;
  const re = /<!--[\s\S]*?-->|<!\[CDATA\[[\s\S]*?\]\]>|<![^>]*>|<\?[\s\S]*?\?>|<\/\s*([A-Za-z][\w:.-]*)\s*>|<([A-Za-z][\w:.-]*)((?:\s+[^\s=/>]+(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?)*)\s*(\/?)>|([^<]+)|</g;
  const root: SvgNode = { tag: '#root', attrs: {}, children: [] };
  const stack: SvgNode[] = [root];
  let n = 0;
  for (const m of s.matchAll(re)) {
    if (++n > MAX_TOKENS) return null;
    const [, close, open, attrText, selfClose, text] = m;
    if (close) {
      for (let i = stack.length - 1; i > 0; i--) if (stack[i].tag === close) { stack.length = i; break; }
    } else if (open) {
      const node: SvgNode = { tag: open, attrs: parseAttrs(attrText ?? ''), children: [] };
      stack[stack.length - 1].children.push(node);
      if (!selfClose) stack.push(node);
    } else if (text !== undefined) {
      const top = stack[stack.length - 1];
      top.text = (top.text ?? '') + decode(text);
    }
  }
  const find = (node: SvgNode): SvgNode | null => {
    for (const c of node.children) { if (c.tag === 'svg') return c; }
    return null;
  };
  return find(root);
}

/* keep only what's on the allowlist */
export function cleanSvg(node: SvgNode): SvgNode | null {
  if (!TAGS.has(node.tag)) return null;
  const attrs: Record<string, string> = {};
  for (const [k, v] of Object.entries(node.attrs)) {
    if (ATTRS.has(k) && !BAD_VALUE.test(v) && SAFE_VALUE.test(v)) attrs[k] = v.trim();
  }
  const style = node.attrs.style;
  if (style) {
    for (const decl of style.split(';')) {
      const i = decl.indexOf(':');
      if (i < 0) continue;
      const prop = decl.slice(0, i).trim().toLowerCase(), value = decl.slice(i + 1).trim();
      if (STYLE_PROPS.has(prop) && value && !BAD_VALUE.test(value) && SAFE_VALUE.test(value) && !(prop in attrs)) attrs[prop] = value;
    }
  }
  const out: SvgNode = { tag: node.tag, attrs, children: [] };
  if (node.tag === 'title' || node.tag === 'desc') { out.text = (node.text ?? '').trim().slice(0, 200); return out; }
  for (const c of node.children) { const k = cleanSvg(c); if (k) out.children.push(k); }
  return out;
}

/* scale with CSS: the drawing's own size becomes the viewBox */
export function finishSvg(svg: SvgNode): SvgNode {
  const attrs = { ...svg.attrs };
  if (!attrs.viewBox) {
    const w = parseFloat(attrs.width || ''), h = parseFloat(attrs.height || '');
    if (w > 0 && h > 0) attrs.viewBox = `0 0 ${w} ${h}`;
  }
  delete attrs.width;
  delete attrs.height;
  attrs['shape-rendering'] = 'crispEdges';
  return { ...svg, attrs };
}

export function sanitizeSvgTree(markup: string): SvgNode | null {
  const tree = parseSvg(markup);
  const clean = tree && cleanSvg(tree);
  return clean ? finishSvg(clean) : null;
}

export function sanitizeSvg(markup: string, doc: Document = document): SVGSVGElement | null {
  const tree = sanitizeSvgTree(markup);
  if (!tree) return null;
  const make = (n: SvgNode): Element => {
    const el = doc.createElementNS(SVG_NS, n.tag);
    for (const [k, v] of Object.entries(n.attrs)) el.setAttribute(k, v);
    if (n.text) el.textContent = n.text;
    for (const c of n.children) el.appendChild(make(c));
    return el;
  };
  const svg = make(tree) as SVGSVGElement;
  svg.setAttribute('focusable', 'false');
  return svg;
}
